// src/components/TestimonialsSection.js
import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext'; // ✅ Added extra ../
import { db } from '../../firebase'; // ✅ Added extra ../
import { 
    collection, query, where, orderBy, limit, onSnapshot, addDoc, serverTimestamp 
} from 'firebase/firestore';
import './TestimonialsSection.css';

const TestimonialsSection = () => {
    const { user } = useAuth();
    const [realReviews, setRealReviews] = useState([]);
    
    // Form State
    const [isWriting, setIsWriting] = useState(false);
    const [rating, setRating] = useState(5);
    const [reviewText, setReviewText] = useState('');
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [loading, setLoading] = useState(false);

    // 1. Fetch APPROVED reviews from Firestore
    useEffect(() => {
        const q = query(
            collection(db, 'testimonials'),
            where('status', '==', 'approved'), // Only show approved
            orderBy('createdAt', 'desc'),
            limit(6)
        );

        const unsub = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRealReviews(data);
        });

        return () => unsub();
    }, []);

    // 2. Handle Secure Submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMsg('');
        setLoading(true);

        // SECURITY: Link Detection Regex
        const linkRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)|(\.com|\.net|\.org|\.io|\.app)/gi;
        
        if (linkRegex.test(reviewText)) {
            setError("⚠️ Security Risk: Links are not allowed. Please remove any URLs.");
            setLoading(false);
            return;
        }

        if (reviewText.trim().length < 10) {
            setError("Review is too short. Please tell us a bit more (10+ characters).");
            setLoading(false);
            return;
        }

        try {
            await addDoc(collection(db, 'testimonials'), {
                uid: user.uid,
                displayName: user.displayName || user.email.split('@')[0], // Fallback to email prefix
                text: reviewText.trim(),
                rating: Number(rating),
                status: 'pending', // <--- This hides it until you approve it
                role: 'Verified Member', // Tag used in the card
                createdAt: serverTimestamp()
            });

            setSuccessMsg("Review submitted for moderation. Thank you!");
            setReviewText('');
            setTimeout(() => {
                setIsWriting(false);
                setSuccessMsg('');
            }, 3000); // Close form after 3 seconds on success
        } catch (err) {
            console.error("Review Error:", err);
            setError("Could not submit. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    // Static reviews (Your original ones) - used as fallback or base content
    const staticReviews = [
        {
            id: 'static-1',
            initials: 'KA',
            name: 'Kwesi A.',
            role: 'High-stakes football bettor',
            rating: 4,
            text: "I used to overbuild slips based on feelings. With AiPredict I only see slips that match the edge filters I set, so I stay calm and consistent.",
            tagIcon: 'solar:soccer-linear',
            tagName: 'Football'
        },
        {
            id: 'static-2',
            initials: 'NM',
            name: 'Nana M.',
            role: 'Crypto & odds watcher',
            rating: 5,
            text: "The crypto pairs and football odds on one layout is everything. I set alerts for both, and when the models ping me I already know the context.",
            tagIcon: 'solar:bitcoin-coin-linear',
            tagName: 'Crypto'
        },
        {
            id: 'static-3',
            initials: 'AD',
            name: 'Ama D.',
            role: 'Bankroll-focused planner',
            rating: 4,
            text: "I like that AiPredict shows slip health and bankroll sync together. It forces me to think in terms of risk per slip instead of just chasing big odds.",
            tagIcon: 'solar:wallet-linear',
            tagName: 'Bankroll'
        }
    ];

    // Merge: Real reviews first, then static ones (limit total visuals if needed)
    // If you have real reviews, they push the static ones down or you can replace them.
    // Here we simply display Real Reviews + Static Reviews.
    const displayReviews = [...realReviews, ...staticReviews];

    return (
        <section className="testimonials">
            <div className="testimonials__container">
                {/* Intro Text */}
                <div className="testimonials__header">
                    <p className="testimonials__eyebrow">
                        <Icon icon="solar:users-group-rounded-linear" className="testimonials__eyebrow-icon" />
                        <span>Signals that people actually use</span>
                    </p>
                    <h2 className="testimonials__title">
                        “Helps me stay disciplined<br />instead of chasing every slip.”
                    </h2>
                    <p className="testimonials__subtitle">
                        AiPredict is built for focused bettors and traders who want structure, not spam.
                        Here's how early users describe working with the models day to day.
                    </p>

                    {/* Write Review Button (Only if Logged In & Not Writing) */}
                    {user && !isWriting && !successMsg && (
                        <button 
                            onClick={() => setIsWriting(true)} 
                            className="testimonials__write-btn"
                            style={{ 
                                marginTop: '20px', 
                                padding: '10px 20px', 
                                background: 'rgba(56, 189, 248, 0.1)', 
                                color: '#38bdf8', 
                                border: '1px solid #38bdf8',
                                borderRadius: '20px',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '0.9rem',
                                fontWeight: '600'
                            }}
                        >
                            <Icon icon="solar:pen-new-square-linear" /> Write a Review
                        </button>
                    )}
                </div>

                {/* Submission Form Area */}
                {isWriting && (
                    <div className="testimonials__form-wrap" style={{ maxWidth: '500px', margin: '0 auto 40px', textAlign: 'left', background: '#1e293b', padding: '24px', borderRadius: '16px', border: '1px solid #334155' }}>
                        <h3 style={{ color: '#fff', marginBottom: '16px', fontSize: '1.1rem' }}>Share your experience</h3>
                        
                        <form onSubmit={handleSubmit}>
                            {/* Stars */}
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <Icon 
                                        key={star} 
                                        icon="solar:star-bold" 
                                        style={{ 
                                            cursor: 'pointer', 
                                            color: rating >= star ? '#fbbf24' : '#475569', 
                                            fontSize: '1.5rem' 
                                        }}
                                        onClick={() => setRating(star)}
                                    />
                                ))}
                            </div>

                            <textarea
                                placeholder="What features help you the most? (No links allowed)"
                                value={reviewText}
                                onChange={(e) => setReviewText(e.target.value)}
                                rows={4}
                                style={{ 
                                    width: '100%', 
                                    padding: '12px', 
                                    borderRadius: '8px', 
                                    border: '1px solid #334155', 
                                    background: '#0f172a', 
                                    color: '#fff', 
                                    marginBottom: '12px',
                                    fontFamily: 'inherit'
                                }}
                            />

                            {error && (
                                <div style={{ color: '#ef4444', fontSize: '0.9rem', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Icon icon="solar:danger-triangle-bold" /> {error}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <button 
                                    type="button" 
                                    onClick={() => setIsWriting(false)}
                                    style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={loading}
                                    style={{ 
                                        background: '#38bdf8', 
                                        border: 'none', 
                                        padding: '8px 20px', 
                                        borderRadius: '8px', 
                                        color: '#0f172a', 
                                        fontWeight: 'bold', 
                                        cursor: 'pointer',
                                        opacity: loading ? 0.7 : 1
                                    }}
                                >
                                    {loading ? 'Submitting...' : 'Submit'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Success Message */}
                {successMsg && (
                    <div style={{ 
                        maxWidth: '500px', 
                        margin: '0 auto 40px', 
                        padding: '16px', 
                        background: 'rgba(74, 222, 128, 0.1)', 
                        border: '1px solid #4ade80', 
                        borderRadius: '12px', 
                        color: '#4ade80', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        gap: '10px' 
                    }}>
                        <Icon icon="solar:check-circle-bold" fontSize="1.2rem" />
                        {successMsg}
                    </div>
                )}

                {/* Grid */}
                <div className="testimonials__grid">
                    
                    {displayReviews.map((item) => {
                        // Handle data structure difference between Static (local) and Real (Firestore)
                        const isReal = !!item.uid; // Real reviews have a UID
                        const initials = isReal ? (item.displayName || 'U').substring(0,2).toUpperCase() : item.initials;
                        const name = isReal ? item.displayName : item.name;
                        const role = isReal ? 'Verified Member' : item.role;
                        const text = item.text;
                        const starCount = item.rating || 5;
                        const tagIcon = isReal ? 'solar:verified-check-bold' : item.tagIcon;
                        const tagName = isReal ? 'Community' : item.tagName;

                        return (
                            <article key={item.id} className="testimonials__card">
                                <div className="testimonials__card-top">
                                    <div className="testimonials__avatar">
                                        <span>{initials}</span>
                                    </div>
                                    <div className="testimonials__identity">
                                        <p className="testimonials__name">{name}</p>
                                        <p className="testimonials__role">{role}</p>
                                    </div>
                                    <div className="testimonials__rating">
                                        {[...Array(5)].map((_, i) => (
                                            <Icon 
                                                key={i} 
                                                icon="solar:star-bold" 
                                                className={`testimonials__star ${i < starCount ? '' : 'testimonials__star--dim'}`} 
                                            />
                                        ))}
                                    </div>
                                </div>
                                <p className="testimonials__quote">
                                    "{text}"
                                </p>
                                <div className="testimonials__tags">
                                    <span className="testimonials__tag">
                                        <Icon icon={tagIcon} /> {tagName}
                                    </span>
                                </div>
                            </article>
                        );
                    })}

                </div>
            </div>
        </section>
    );
};

export default TestimonialsSection;